import pnlProject from '../content/blogs/pnl/registry'

const projects = [
  {
    title: 'P&L App',
    description:
      'A neuroscience-based trading journal and discipline platform with AI-powered insights, auto-journal sync with 20+ brokerages, and Discipline Mode.',
    liveLink: pnlProject.websiteLink,
    slug: pnlProject.slug,
    images: (pnlProject.screenshots || []).map((s) => s.src),
    appStoreLink: pnlProject.appStoreLink,
    playStoreLink: pnlProject.playStoreLink,
    featuredLayout: 'mobile',
    caseStudy: [
      {
        title: 'Overview',
        body: 'P&L App is a mobile trading journal and discipline platform for iOS and Android. It brings trade history, performance analytics, behavioral reflection, and protective tools into one place, helping traders understand their habits as well as their outcomes.',
      },
      {
        title: 'The challenge',
        body: 'Trade records are spread across brokerages, while manual journaling takes time and often misses the context behind a decision. A conventional tracker can report wins and losses after the fact, but it does little to help a user recognize repeated patterns or pause before an impulsive trade.',
      },
      {
        title: 'My role',
        body: 'I was the full-stack team lead and deployment engineer. I led the work from architecture and implementation through team coordination, production infrastructure, and the iOS and Android store releases.',
      },
      {
        title: 'What I built',
        body: 'I built the cross-platform React Native app and Node.js backend, with Zustand state management, SQL data storage, and Redis with BullMQ for background jobs. SnapTrade connects more than 20 brokerages for automatic trade journaling. On Android, I implemented Discipline Mode as a Kotlin native module using Accessibility Services and usage monitoring to block selected trading apps for a chosen period. The product also includes performance analytics, a journal calendar, AI-personalized notifications through OpenRouter, trader community chat, and RevenueCat subscriptions.',
      },
      {
        title: 'Result',
        body: 'The app shipped on iOS and Android, with more than 150 downloads in its first month. The delivered product combines automated trade imports, analytics, community features, and an active intervention tool rather than leaving traders with record keeping alone.',
      },
    ],
  },
  {
    title: "QuickStart AI",
    description: `QuickStart AI is a multi-tenant platform for businesses to build, deploy, and operate AI agents across website chat, staff inbox, voice, campaigns, and lead generation. It brings agent configuration, customer conversations, and operational workflows together in one workspace.`,

    liveLink: "https://quickstartai.com/",
    demoLink: "https://youtu.be/MwNOuVCunjQ",
    slug: "quickstart-ai",
    featuredImage: "/projects/quickstart-ai-cover.png",
    images: [
      "/projects/quickstart-ai/quickstart-agent-studio.png",
      "/projects/quickstart-ai/quickstart-team-inbox.png",
      "/projects/quickstart-ai/quickstart-campaigns-leads.png",
    ],
    featuredLayout: 'desktop',
    caseStudy: [
      {
        title: "Overview",
        body: "QuickStart AI gives each business workspace the tools to configure agents with instructions, models, tools, and company knowledge, then serve customers through the website widget, staff inbox, and voice channels. The same platform also supports campaigns and automated lead generation.",
      },
      {
        title: "The challenge",
        body: "Businesses need agents that can use their own knowledge and tools while fitting into real customer workflows. That requires more than a chat interface: the platform must keep tenant data isolated, make conversations available for staff handoff, support channel integrations, and coordinate background work reliably.",
      },
      {
        title: "My role",
        body: "I created QuickStart AI and worked across the product architecture and implementation, including the web application, gateway, backend services, and embeddable widget. I focused on building a coherent multi-tenant platform with clear service boundaries and production-oriented reliability, security, and observability.",
      },
      {
        title: "Platform capabilities",
        body: "The platform includes workspace and agent configuration, a publishable-key website widget, conversation inbox with human handoff, inbound and outbound voice paths, campaign management, and lead generation with evidence-backed research. Agents can use workspace knowledge and custom tools. A public gateway fronts the service architecture, while shared contracts and tenant-scoped APIs keep integrations consistent.",
      },
      {
        title: "Engineering focus",
        body: "QuickStart AI is being built as a production engineering system, with tenant isolation, idempotent APIs, fair job scheduling, observability, and failure handling treated as core requirements. Its service-based architecture separates identity, workspace, gateway, agent, conversation, voice, and lead generation responsibilities, making the platform easier to evolve as channels and workflows grow.",
      },
    ],
  },
  {
    title: "R&B TEAUSA",
    description: `R&B Tea’s franchise platform supports a global network of more than 1,500 locations. The previous website made it difficult to keep menus and store locations current and to coordinate between administrators and franchisees. We built a web experience for menu and location management, franchise communication, and onboarding; the platform generated more than 100 partnership requests.`,
    liveLink: "https://rbteausa.com/",
    slug: "rb-teausa",
    featuredImage: "/projects/rbteausa-dashboard-cover.png",
    images: [
      "/projects/banner.png",
      "/projects/rbteausahome.png",
      "/projects/chat.png",
      "/projects/drinks.png",
      "/projects/locations.png",
    ],
    featuredLayout: 'desktop',
    caseStudy: [
      {
        title: "Overview",
        body: "R&B Tea is a large bubble tea brand with a franchise network spanning more than 1,500 locations. The website serves customers looking for drinks and nearby stores, while also supporting the business and franchise relationships behind that network.",
      },
      {
        title: "The challenge",
        body: "The existing site did not give the team an effective way to keep menus up to date, add franchise locations to the store map, or communicate with franchise operators. Those gaps made routine updates harder and left important customer and partnership journeys without a clear, centralized online experience.",
      },
      {
        title: "My role",
        body: "I built the R&B Tea USA franchise platform, including the customer-facing website and the operational workflows needed to support a large franchise network. The work connected the public discovery experience with administration, franchise communication, and onboarding needs.",
      },
      {
        title: "What we delivered",
        body: "We created a responsive brand website with drink and menu content, a store locations experience with map and location information, and tools for administrators to update menus and add locations. We also built real-time chat and onboarding workflows to improve communication between the central team and franchisees. The project images show the homepage, drinks, locations, and chat experience.",
      },
      {
        title: "Result",
        body: "The platform gave R&B Tea a more complete online presence for customers and franchise operators, with clearer paths to find drinks, locate stores, and engage with the franchise network. The project generated more than 100 partnership requests and supported a network of over 1,500 locations.",
      },
    ],
  },
  {
    title: "B0Bot — Cybersecurity News Intelligence",
    description: `A cybersecurity news intelligence platform rebuilt during Google Summer of Code 2026 with C2SI (Ceylon Computer Science Institute). The project turns continuously updated security feeds into searchable, enriched articles, an AI-assisted news experience, and subscriber email digests. My work focused on replacing the legacy backend and building the ingestion and notification infrastructure that powers the platform.`,
    slug: "b0bot-gsoc-c2si",
    repoLink: "https://github.com/c2siorg/b0bot",
    reportLink: "https://github.com/c2siorg/GSoC/blob/master/GSoC-2026/07-Muhammad-Aqib-B0Bot.md",
    gsocLink: "https://summerofcode.withgoogle.com/programs/2026/projects/7sf7V0bk",
    images: [
      "/projects/b0bot/b0bot-cover.png",
      "/projects/b0bot/landing.png",
      "/projects/b0bot/architecture.png",
      "/projects/b0bot/dashboard.png",
    ],
    caseStudy: [
      {
        title: "The challenge",
        body: "The legacy B0Bot backend depended on a single monolithic application, Pinecone, hand-run synchronization scripts, and hardcoded news collection. That made the data pipeline harder to operate and left ingestion tightly coupled to the API. The project needed a repeatable local stack and a reliable path from security news feeds to searchable articles and subscriber digests.",
      },
      {
        title: "My role",
        body: "As a Google Summer of Code 2026 contributor with C2SI, I owned backend infrastructure: Docker Compose, the PostgreSQL schema, ingestion-service, notification-service, BullMQ queue contracts, and the ingestion data pipeline. I coordinated shared schema and service boundaries with the contributor working on the API, agent pipeline, and frontend.",
      },
      {
        title: "What I built",
        body: "I migrated the backend to three services—API, ingestion, and notifications—running with PostgreSQL/pgvector and Redis under Docker Compose. The ingestion worker polls configurable RSS sources, normalizes URLs, deduplicates with SHA-256 hashes, queues jobs through BullMQ, creates sentence-transformer embeddings, enriches articles with CVE/severity/system/topic metadata, and stores them for search. I also built daily and weekly email digests with unsubscribe handling, delivery records, and idempotency checks. Dynamic RSS sources let administrators add feeds without a code deployment.",
      },
      {
        title: "Result",
        body: "B0Bot now runs as a repeatable local stack without mandatory cloud services. Security articles flow from RSS polling through enrichment and vector indexing into the API experience, while digest notifications reach subscribers on a schedule. The service boundaries, queue tracking, and processed-job checks make the pipeline easier to operate and safer to retry. Core backend deliverables were merged into the C2SI repository; the linked final report lists the work and PR status in detail.",
      },
    ],
  },
  {
    title: "Dental Strivers LMS",
    description: `A learning and assessment platform for Dental Strivers, supporting internationally trained dentists preparing for Canada's Assessment of Fundamental Knowledge (AFK) exam. Over a two-year engagement, I helped build and operate the platform across course delivery, protected video, quizzes, notifications, shared libraries, and production infrastructure.`,
    liveLink: "https://dentalstrivers.ca/",
    slug: "dental-strivers-lms",
    caseStudy: [
      {
        title: "Overview",
        body: "Dental Strivers delivers remote preparation for internationally trained dental professionals pursuing Canadian licensure. The platform supports course materials, recorded lessons, quizzes, and mock exams so students can prepare from different locations and revisit learning content on demand.",
      },
      {
        title: "The challenge",
        body: "The team needed a dependable platform for delivering protected video lessons and online assessments to successive student batches. It also needed to manage different video quality levels and segmented playback, protect course content, notify learners, and support safe releases to both staging and production environments.",
      },
      {
        title: "My role",
        body: "I worked across the product lifecycle, from client requirements and feature development through deployment and ongoing production operations. I contributed to the video delivery system, assessment experience, shared library, notifications, and release automation.",
      },
      {
        title: "What we built",
        body: "We implemented HLS video delivery that converts source lessons into multiple quality levels and serves them as chunks for adaptive playback, with encryption at the server. We added a quiz platform with video, screen, and audio recording plus lightweight runtime ML signals to flag possible assessment-integrity issues for review. We also delivered a notification system, a shared library, and CI/CD pipelines for staging and production deployments.",
      },
      {
        title: "Result",
        body: "The platform has been in use for about two years and has supported two course batches. The team handles the full cycle—from client requirements and development to staging, production deployment, and ongoing operation—across a working education and assessment service.",
      },
    ],
    images: [
      "/projects/dental_1.png",
      "/projects/dental_2.png",
      "/projects/dental_3.png",
      "/projects/dental_4.png",
    ],
  },
  {
    title: "Agriturismo Santommaso",
    description: `Agriturismo San Tommaso needed a stronger way to present its Italian resort and convert interest into bookings. We built a localized booking website integrated with Guesty and Stripe, with property and offer pages, dynamic availability, and a guided reservation flow. Localization work doubled conversion.`,
    liveLink: "https://booking.agriturismo-santommaso.com/",
    slug: "agriturismo-santommaso",
    images: [
      "/projects/bookingHome.png",
      "/projects/bookingAbout.png",
      "/projects/bookingDates.png",
      "/projects/bookingOffer.png",
    ],
    caseStudy: [
      {
        title: "Overview",
        body: "Agriturismo San Tommaso is an Italian resort whose website needs to do two things well: communicate the character of the property and help visitors complete a reservation. The project delivered a dedicated booking experience connected to the client’s accommodation operations.",
      },
      {
        title: "The challenge",
        body: "The client needed a more effective platform for presenting the resort and its offers, while giving prospective guests a clear path from browsing to checking dates and booking. Availability and reservation details also needed to stay connected to the existing Guesty workflow.",
      },
      {
        title: "My role",
        body: "I worked on the booking website and its integration with Guesty and Stripe, shaping the experience from property discovery through availability selection and reservation. I also worked on localization to make the booking journey more effective for its audience.",
      },
      {
        title: "What we delivered",
        body: "We built resort and offer pages, dynamic availability calendars, and a customer-friendly reservation flow using the Guesty API. Stripe supports the payment part of the journey. The experience brings the property presentation, date selection, and booking steps together, with localization applied to improve usability for visitors.",
      },
      {
        title: "Result",
        body: "The resort gained a dedicated online channel for presenting its accommodation and receiving bookings through an integrated Guesty and Stripe flow. The localized experience doubled conversion, making the improvements measurable as well as more useful for guests.",
      },
    ],
  },
  {
    title: "Siege Builders",
    description: `I designed and built a portfolio website for Siege Builders, a gaming company associated with Clash of Clans. The site carries the game’s energetic visual direction into a focused company showcase, with sections for the studio, partnership information, and game media.`,
    liveLink: "https://dancing-babka-f6e2a6.netlify.app/",
    slug: "siege-builders",
    images: [
      "/projects/gameHome.png",
      "/projects/gamePartner.png",
      "/projects/gameVideo.png",
    ],
    caseStudy: [
      {
        title: "Overview",
        body: "Siege Builders needed a web presence that introduced the gaming company and reflected the visual identity of its flagship Clash of Clans themed work. The result is a compact portfolio site built to make the studio and its game related content easy to explore.",
      },
      {
        title: "The challenge",
        body: "A generic corporate layout would not communicate the character of a game studio. The site needed to feel connected to the game’s world while remaining legible as a company portfolio, and it needed to present the studio, partnership information, and media in a coherent flow.",
      },
      {
        title: "My role",
        body: "I crafted the portfolio website, shaping its visual direction around the client’s game and organizing the content into a concise browsing experience. The work focused on translating the existing game aesthetic into a branded web presentation.",
      },
      {
        title: "What I built",
        body: "I created the site’s visual presentation and page structure, including a company introduction, a partnership section, and a game video area. The design uses strong game-inspired visuals and a direct content hierarchy so visitors can understand the studio and move quickly to its featured material.",
      },
      {
        title: "Result",
        body: "Siege Builders received a focused portfolio site that presents its gaming work in a visual style aligned with Clash of Clans. The live site gives visitors a single place to discover the company, review partnership information, and view game media.",
      },
    ],
  },
];

export default projects;
